/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { DateTime } from "luxon";
import { Location } from "react-router-dom";
import {
  ConstraintViolationError,
  DateRange,
  HHmm,
  IConstraintViolationMessage,
  IErrorMessage,
  ISite,
  OptionTypeBase,
  StorageClass,
  ThemeSetting,
  isValid,
  prodModeLog,
  SeriesFilterRuleType,
} from "@advapacs/common";
import { Account } from "../pages/login/ducks/slices";
import {
  ImageLifecycleManagementTaskStatus,
  MigrationIndexedStudyStatus,
  MigrationJobStatus,
  QueryGranularityOption,
  TIME_OPTIONS,
} from "../constant";
import { CartType } from "../types/billing";
import { API_REGION_OVERRIDE } from "../config/constants";

/* eslint-disable  */
export const findConstraintViolationErrorMessage = (
  errorMessage: IErrorMessage | IConstraintViolationMessage | null,
  field: string
): ConstraintViolationError | undefined | null => {
  if (errorMessage?.type === "ConstraintViolationException")
    return (errorMessage as IConstraintViolationMessage).errors.find((e) =>
      e.field.includes(field)
    );
  return null;
};

export const uint6ToB64 = (nUint6: any) => {
  return nUint6 < 26
    ? nUint6 + 65
    : nUint6 < 52
    ? nUint6 + 71
    : nUint6 < 62
    ? nUint6 - 4
    : nUint6 === 62
    ? 43
    : nUint6 === 63
    ? 47
    : 65;
};

export const arrayBufferToBase64Basic = async (data: any) => {
  const blobData = new Blob([data]);
  const reader = new FileReader();
  reader.readAsDataURL(blobData);
  await new Promise<void>((resolve) => (reader.onload = () => resolve()));
  const base64url = reader.result;
  /*
  The result looks like
  "data:application/octet-stream;base64,",
  so we split off the beginning:
  */
  // @ts-ignore
  return base64url.split(",", 2)[1];
};

export const arrayBufferToBase64 = (aBytes: any) => {
  const eqLen = (3 - (aBytes.length % 3)) % 3;
  let sB64Enc = "";

  for (
    let nMod3, nLen = aBytes.length, nUint24 = 0, nIdx = 0;
    nIdx < nLen;
    nIdx++
  ) {
    nMod3 = nIdx % 3;
    nUint24 |= aBytes[nIdx] << ((16 >>> nMod3) & 24);
    if (nMod3 === 2 || aBytes.length - nIdx === 1) {
      sB64Enc += String.fromCharCode(
        uint6ToB64((nUint24 >>> 18) & 63),
        uint6ToB64((nUint24 >>> 12) & 63),
        uint6ToB64((nUint24 >>> 6) & 63),
        uint6ToB64(nUint24 & 63)
      );
      nUint24 = 0;
    }
  }
  return eqLen === 0
    ? sB64Enc
    : sB64Enc.substring(0, sB64Enc.length - eqLen) + (eqLen === 1 ? "=" : "==");
};

export const hasPermission = (
  permissions: string[] = [],
  can?: string[]
): boolean => {
  return !can || !!can.some((item) => permissions.includes(item));
};

export const isOwner = (account: Account | null): boolean | undefined => {
  return account?.owner;
};

export const parseDateFromString = (
  dirtyDate?: string,
  fmt?: string,
  isTimeValue?: boolean
): Date | any | undefined => {
  const isValidDate = isValid(dirtyDate);

  if (isTimeValue || (dirtyDate && isValidDate)) {
    const date = fmt
      ? DateTime.fromFormat(dirtyDate as string, fmt).toJSDate()
      : DateTime.fromISO(dirtyDate as string).toJSDate();

    return isValid(date) ? date : undefined;
  }
  if (!isValidDate && dirtyDate) return "Invalid Value";
  return undefined;
};

export const parseDateRange = (
  dirtyRange: {
    startDate: string;
    endDate: string;
    key: string;
  },
  nullable = false
): DateRange => {
  return {
    startDate:
      parseDateFromString(dirtyRange?.startDate) ||
      (nullable ? undefined : new Date()),
    endDate:
      parseDateFromString(dirtyRange?.endDate) ||
      (nullable ? undefined : new Date()),
  };
};

export const capitalizeFirstLetter = (str?: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const currencyFormat = (value: string, symbol = "$"): string => {
  return `${symbol}${value.replace(/\.?0+$/, "")}`;
};

export const currencyFormatWithCode = (
  value: string,
  code: string,
  symbol = "$"
): string => {
  let val = value ? value : "";
  return `${symbol}${val.replace(/\.?0+$/, "")}${code}`;
};

export const convertTimeInput = (value: string): string => {
  if (!value) {
    return "";
  }
  const hh = value.slice(0, 2);
  const mm = value.slice(2, 4);
  const ss = value.slice(4, 6);
  return `${hh}:${mm}:${ss}`;
};

export const convertStringToTimeStr = (
  value: string,
  timeFmt?: string
): string => {
  if (!value) {
    return "";
  }

  const hh = value.slice(0, 2);
  const mm = value.slice(2, 4);
  const ss = value.slice(4, 6);
  if (!timeFmt) return `${hh}:${mm}:${ss}`;
  if (timeFmt === HHmm) return `${hh}:${mm}`;
  else {
    const hour = parseInt(hh, 10) % 12 ? parseInt(hh, 10) % 12 : 12;
    return `${hour >= 10 ? hour : "0" + hour}:${mm} ${
      parseInt(hh, 10) >= 12 ? "PM" : "AM"
    }`;
  }
};

export const getCreditCardIconClass = (cardType: string): string => {
  switch (cardType) {
    case CartType.VISA: {
      return "fa-cc-visa";
    }
    case CartType.JCB: {
      return "fa-cc-jcb";
    }
    case CartType.AMERICAN_EXPRESS: {
      return "fa-cc-amex";
    }
    case CartType.DISCOVER: {
      return "fa-cc-discover";
    }
    case CartType.MAESTRO: {
      return "icon-credit-card";
    }
    case CartType.MASTERCARD: {
      return "fa-cc-mastercard";
    }
    default:
      return "icon-credit-card";
  }
};

export const checkMatchPath = (
  redirectPath: string,
  currentLocation: Location
): boolean => {
  return (
    redirectPath.includes(currentLocation?.pathname) ||
    currentLocation.pathname.includes(redirectPath)
  );
};

export const getRegion = (): string => {
  let region: string | null = API_REGION_OVERRIDE;
  if (!region) {
    // Region is resolved when the application first loads and cached in local storage.
    // There is no need to resolve it again.
    region = localStorage.getItem(`${window.location.host}.region`);
  }

  if (!region) {
    // eslint-disable-next-line no-console
    console.error("Cannot find the region in the cache");
    return "";
  }
  return region;
};

export const findSubmultipleOfNumber = (
  num: number,
  currentOption?: string
): OptionTypeBase[] => {
  const arr = [];
  for (let index = 1; index <= num; index++) {
    if (num % index === 0) arr.push(index);
  }
  if (
    currentOption &&
    arr.filter((x) => x === parseInt(currentOption, 10)).length === 0
  ) {
    arr.push(parseInt(currentOption, 10));
  }
  return arr.map((x) => {
    return {
      label: `${x}H`,
      value: `${x}H`,
    } as OptionTypeBase;
  });
};

export const getValueByKey = (key: string): MigrationJobStatus => {
  const indexOfS = Object.keys(MigrationJobStatus).indexOf(
    key as unknown as MigrationJobStatus
  );

  return <MigrationJobStatus>Object.values(MigrationJobStatus)[indexOfS];
};

export const getValueByKeyIndexed = (
  key: string
): MigrationIndexedStudyStatus => {
  const indexOfS = Object.keys(MigrationIndexedStudyStatus).indexOf(
    key as unknown as MigrationIndexedStudyStatus
  );

  return <MigrationIndexedStudyStatus>(
    Object.values(MigrationIndexedStudyStatus)[indexOfS]
  );
};

export const getKeyByValueIndexed = (value: string): string => {
  const indexOfS = Object.values(MigrationIndexedStudyStatus).indexOf(
    value as unknown as MigrationIndexedStudyStatus
  );

  return Object.keys(MigrationIndexedStudyStatus)[indexOfS];
};

export const getKeyByValue = (value: string): string => {
  const indexOfS = Object.values(MigrationJobStatus).indexOf(
    value as unknown as MigrationJobStatus
  );

  return Object.keys(MigrationJobStatus)[indexOfS];
};

export const calcSecondRowOptions = (
  dateRange: DateRange,
  currentOption?: string
): OptionTypeBase[] => {
  if (dateRange.startDate && dateRange.endDate) {
    const end = new Date(dateRange.startDate);
    end.setDate(end.getDate() + 1);
    const start = new Date(dateRange.endDate);
    const numberOfHours = end.getHours() + 24 - start.getHours();
    if (numberOfHours === 1 || numberOfHours === 0) {
      return [
        {
          label: QueryGranularityOption.ONE_HOUR,
          value: QueryGranularityOption.ONE_HOUR,
        },
      ];
    }

    return findSubmultipleOfNumber(numberOfHours, currentOption);
  }
  return TIME_OPTIONS;
};

export const calcFirstRowOptions = (dateRange: DateRange): OptionTypeBase[] => {
  if (dateRange.startDate && dateRange.endDate) {
    const end = new Date(dateRange.endDate);
    const start = new Date(dateRange.startDate);
    const numberOfHours = end.getHours() - start.getHours();
    if (numberOfHours === 1 || numberOfHours === 0) {
      return [
        {
          label: QueryGranularityOption.ONE_HOUR,
          value: QueryGranularityOption.ONE_HOUR,
        },
      ];
    }

    return findSubmultipleOfNumber(numberOfHours);
  }
  return TIME_OPTIONS;
};

export const formatTime = (
  date: Date,
  showAMPM = true,
  isNextDay = false
): string => {
  let hoursOfDate = date.getHours();
  const minutesOfDate = date.getMinutes();
  const timeFormat = hoursOfDate >= 12 ? "PM" : "AM";
  hoursOfDate %= 12;
  hoursOfDate = hoursOfDate || 12; // the hour '0' should be '12'
  const minutes = minutesOfDate < 10 ? `0${minutesOfDate}` : minutesOfDate;
  const hours = hoursOfDate < 10 ? `0${hoursOfDate}` : hoursOfDate;
  const resultTimeFormat = isNextDay
    ? `${hours}:${minutes}ND`
    : `${hours}:${minutes}`;
  return showAMPM ? `${resultTimeFormat} ${timeFormat}` : `${resultTimeFormat}`;
};

export const getEndOfTheDay = (day?: Date | null): Date | null => {
  if (!day) return null;
  const result = new Date(day);
  result.setUTCHours(23, 59, 59, 999);
  return result;
};

export const getValueByKeyForILMStatus = (
  key: string
): ImageLifecycleManagementTaskStatus => {
  const indexOfS = Object.keys(ImageLifecycleManagementTaskStatus).indexOf(
    key as unknown as ImageLifecycleManagementTaskStatus
  );

  return <ImageLifecycleManagementTaskStatus>(
    Object.values(ImageLifecycleManagementTaskStatus)[indexOfS]
  );
};

export const getKeyByValueILMStatus = (value: string): string => {
  const indexOfS = Object.values(ImageLifecycleManagementTaskStatus).indexOf(
    value as unknown as ImageLifecycleManagementTaskStatus
  );

  return Object.keys(ImageLifecycleManagementTaskStatus)[indexOfS];
};

export const hasAllOfflineStorageClass = (
  items: {
    storageClasses: StorageClass[];
  }[]
) => {
  if (items.length === 0) return false;
  return items
    .map((s) => s.storageClasses)
    .flat()
    .every((s) => s === StorageClass.OFFLINE);
};

export const hasSomeOfflineStorageClass = (
  items: {
    storageClasses: StorageClass[];
  }[]
) => {
  if (items.length === 0) return false;
  const storagesClasses = items.map((s) => s.storageClasses).flat();
  const offlineSC = storagesClasses.filter((s) => s === StorageClass.OFFLINE);
  return offlineSC.length > 0 && offlineSC.length < storagesClasses.length;
};

const getResolveWorkerUrl = (path: string, URL = "URL") =>
  `new ${URL}(${path}).href`;

export const getRelativeUrlFromDocument = (relativePath: string, umd = false) =>
  getResolveWorkerUrl(
    `'${relativePath}', ${
      umd ? `typeof document === 'undefined' ? location.href : ` : ""
    }document.currentScript && document.currentScript.src || document.baseURI`
  );

export const PatientRoutePath = {
  list: "",
  view: "/:patientId",
  toViewPath: (patientId: string) => `/Patients/${patientId}`,
  create: "/Create",
  edit: "/:patientId/Edit",
  toEditPath: (patientId: string) => `/Patients/${patientId}/Edit`,
};

export const splitPatientName = (patientName: string) => {
  const nameComponents = patientName.split("^");
  return {
    lastName: nameComponents.length >= 1 ? nameComponents[0] : "",
    firstName: nameComponents.length >= 2 ? nameComponents[1] : "",
    middleName: nameComponents.length >= 3 ? nameComponents[2] : "",
    prefix: nameComponents.length >= 4 ? nameComponents[3] : "",
    suffix: nameComponents.length >= 5 ? nameComponents[4] : "",
  };
};

export const reduceUploadPayload = (
  patientId: string | null,
  studyId: string | null,
  orderId: string | null
) => {
  // protect upload wrong document for patient of study or order.
  if (studyId && orderId)
    throw new Error("Something wrong with upload payload local state.");
  if (studyId) return { studyId };
  if (orderId) return { orderId };
  if (patientId) return { patientId };
};

export const uploadDocumentLog = (
  patientId?: string,
  studyId?: string,
  orderId?: string
) => {
  if (studyId && orderId) {
    console.log("DANGEROUS !!!");
    return;
  }
  if (studyId) {
    console.log("Upload document width study.");
    prodModeLog(studyId);
    return;
  } else if (orderId) {
    console.log("Upload document with order.");
    prodModeLog(orderId);
    return;
  }
  if (patientId) {
    console.log("Upload document with patient.");
    prodModeLog(patientId);
    return;
  }

  console.error(
    "Uploading document without request key ID, Should check function uploading."
  );
};

export const getThemeConfig = (theme: ThemeSetting) => {
  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  let localBackgroundTheme = theme;
  switch (theme) {
    case ThemeSetting.SYSTEM:
      localBackgroundTheme = !isDark ? ThemeSetting.LIGHT : ThemeSetting.DARK;
      break;
    case ThemeSetting.DARK:
      localBackgroundTheme = ThemeSetting.DARK;
      break;
    case ThemeSetting.LIGHT:
      localBackgroundTheme = ThemeSetting.LIGHT;
      break;

    default:
      break;
  }
  return localBackgroundTheme;
};

export const getSeriesFilterRuleTypeFromPath = (
  pathname: string
): SeriesFilterRuleType | undefined => {
  if (pathname.includes("/PatientPortal")) {
    return SeriesFilterRuleType.PATIENT_PORTAL;
  }
  if (pathname.includes("/ClinicianPortal")) {
    return SeriesFilterRuleType.CLINICIAN_PORTAL;
  }
};

export const getSitesForOrganisations = (
  sites: ISite[],
  organisationIds: string[]
): ISite[] =>
  organisationIds.length === 0
    ? sites
    : sites.filter(
        (s) => s.organisation?.id && organisationIds.includes(s.organisation.id)
      );

export const pruneSitesByOrganisations = <T>(
  selectedSites: T[],
  organisationIds: string[],
  sites: ISite[],
  getSiteId: (item: T) => string | undefined
): T[] => {
  // Note: an empty organisationIds list yields no valid sites (matching sites.filter(() => [].includes(...))),
  // so clearing the organisation selection also clears the site selection.
  const validIds = new Set(
    sites
      .filter(
        (s) => s.organisation?.id && organisationIds.includes(s.organisation.id)
      )
      .map((s) => s.id)
  );
  return selectedSites.filter((item) => {
    const id = getSiteId(item);
    return id ? validIds.has(id) : false;
  });
};
